/**
 * Enhanced Notification Center
 * Multi-channel notification system for diaspora clients
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Drawer,
    IconButton,
    Badge,
    Typography,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    Avatar,
    Chip,
    Button,
    Divider,
    Tab,
    Tabs,
    Switch,
    FormControlLabel,
    Alert,
    Tooltip,
    Menu,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField
} from '@mui/material';
import {
    Notifications,
    NotificationsActive,
    Message,
    Payment,
    Warning,
    CheckCircle,
    Info,
    Error as ErrorIcon,
    Schedule,
    Close,
    Settings,
    MarkEmailRead,
    Delete,
    FilterList,
    Sms,
    WhatsApp,
    Email,
    Phone
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { isPathAllowedForRole } from '../../utils/notificationNav';
import DialogCloseButton from '../common/DialogCloseButton';
import notificationService from '../../services/notificationService';

const EnhancedNotificationCenter = ({ open, onClose }) => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [notifications, setNotifications] = useState([]);
    const [alerts, setAlerts] = useState([]);
    const [selectedTab, setSelectedTab] = useState(0);
    const [filterAnchorEl, setFilterAnchorEl] = useState(null);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    
    // Notification preferences
    const [preferences, setPreferences] = useState({
        email: true,
        sms: false,
        push: true,
        whatsapp: false,
        frequency: 'immediate',
        quiet_hours: { start: '22:00', end: '07:00' },
        alert_types: {
            milestone_updates: true,
            payment_required: true,
            contractor_messages: true,
            quality_alerts: true,
            budget_warnings: true,
            timeline_updates: true
        }
    });

    // Load notifications
    useEffect(() => {
        if (open) {
            loadNotifications();
        }
    }, [open]);

    const loadNotifications = useCallback(async () => {
        setLoading(true);
        try {
            // Load real notifications from API
            const result = await notificationService.getNotifications({
                limit: 50,
                includeArchived: false
            });

            // Transform API notifications to component format
            const transformedNotifications = result.notifications.map(notif => ({
                id: notif.id,
                type: notif.category || notif.type,
                title: notif.title,
                message: notif.message,
                timestamp: notif.created_at,
                read: notif.is_read,
                severity: notif.priority === 'urgent' ? 'error' : notif.priority === 'high' ? 'warning' : 'info',
                color: notif.category === 'payment' ? 'primary' : notif.category === 'alert' ? 'error' : 'info',
                actionable: !!notif.action_url,
                action_text: notif.action_url ? 'View' : null,
                action_url: notif.action_url,
                data: notif.data
            }));

            setNotifications(transformedNotifications);

            // Separate alerts (urgent/high priority notifications)
            const alertNotifications = transformedNotifications.filter(
                notif => notif.severity === 'error' || notif.severity === 'warning'
            );
            setAlerts(alertNotifications);

            // Load preferences
            try {
                const prefs = await notificationService.getPreferences();
                setPreferences({
                    email: prefs.email_enabled,
                    sms: prefs.sms_enabled,
                    push: prefs.push_enabled,
                    whatsapp: prefs.whatsapp_enabled,
                    frequency: prefs.frequency || 'immediate',
                    quiet_hours: preferences.quiet_hours, // Keep existing structure
                    alert_types: preferences.alert_types // Keep existing structure
                });
            } catch (prefError) {
                console.warn('Failed to load notification preferences:', prefError);
            }

        } catch (error) {
            console.error('Error loading notifications:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    // Get notification icon
    const getNotificationIcon = (type) => {
        const icons = {
            milestone: <CheckCircle color="success" />,
            payment: <Payment color="primary" />,
            message: <Message color="info" />,
            alert: <Warning color="warning" />,
            error: <ErrorIcon color="error" />,
            info: <Info color="info" />
        };
        return icons[type] || <Notifications />;
    };

    // Get notification color
    const getNotificationColor = (severity) => {
        const colors = {
            info: 'info',
            success: 'success',
            warning: 'warning',
            error: 'error'
        };
        return colors[severity] || 'default';
    };

    // The bell badge lives in Layout — poke it so the count drops the moment
    // something is read/deleted, not when the drawer closes.
    const refreshBellBadge = () => window.dispatchEvent(new Event('eqaya:unread-refresh'));

    // Mark as read
    const handleMarkAsRead = async (notificationId) => {
        try {
            await notificationService.markAsRead(notificationId);
            setNotifications(prev =>
                prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
            );
            refreshBellBadge();
        } catch (error) {
            console.error('Failed to mark notification as read:', error);
        }
    };

    // Mark all as read
    const handleMarkAllAsRead = async () => {
        try {
            await notificationService.markAllAsRead();
            setNotifications(prev => prev.map(n => ({ ...n, read: true })));
            refreshBellBadge();
        } catch (error) {
            console.error('Failed to mark all as read:', error);
        }
    };

    // Delete notification
    const handleDelete = async (notificationId) => {
        try {
            await notificationService.deleteNotification(notificationId);
            setNotifications(prev => prev.filter(n => n.id !== notificationId));
            refreshBellBadge();
        } catch (error) {
            console.error('Failed to delete notification:', error);
        }
    };

    // Clicking a notification (anywhere on the row) marks it read, and follows
    // its action link when it has one.
    const handleNotificationAction = async (notification) => {
        if (!notification.read) {
            await handleMarkAsRead(notification.id);
        }
        if (!notification?.action_url) return;
        onClose?.();
        // Never follow a link this role can't access (e.g. a stray /admin URL
        // delivered to a client) — it would only land on "Access Denied".
        if (isPathAllowedForRole(notification.action_url, user?.role)) {
            navigate(notification.action_url);
        } else {
            navigate('/dashboard');
        }
    };

    // Save preferences
    const handleSavePreferences = async () => {
        try {
            await notificationService.updatePreferences({
                email_enabled: preferences.email,
                sms_enabled: preferences.sms,
                push_enabled: preferences.push,
                whatsapp_enabled: preferences.whatsapp,
                frequency: preferences.frequency
            });
            setSettingsOpen(false);
        } catch (error) {
            console.error('Error saving preferences:', error);
        }
    };

    // Unread count
    const unreadCount = notifications.filter(n => !n.read).length;

    return (
        <>
            <Drawer
                anchor="right"
                open={open}
                onClose={onClose}
                PaperProps={{
                    sx: { width: { xs: '100%', sm: 450 } }
                }}
            >
                {/* Header */}
                <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <NotificationsActive />
                        <Typography variant="h6">
                            Notifications
                        </Typography>
                        {unreadCount > 0 && (
                            <Chip 
                                label={unreadCount} 
                                size="small" 
                                sx={{ bgcolor: 'rgba(255,255,255,0.3)', color: 'white' }}
                            />
                        )}
                    </Box>
                    <Box>
                        <Tooltip title="Settings">
                            <IconButton 
                                color="inherit" 
                                size="small"
                                onClick={() => setSettingsOpen(true)}
                            >
                                <Settings />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Close">
                            <IconButton color="inherit" size="small" onClick={onClose}>
                                <Close />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>

                {/* Action Bar */}
                <Box sx={{ p: 2, display: 'flex', gap: 1, borderBottom: 1, borderColor: 'divider' }}>
                    <Button 
                        size="small" 
                        startIcon={<MarkEmailRead />}
                        onClick={handleMarkAllAsRead}
                        disabled={unreadCount === 0}
                    >
                        Mark All Read
                    </Button>
                    <Button 
                        size="small" 
                        startIcon={<FilterList />}
                        onClick={(e) => setFilterAnchorEl(e.currentTarget)}
                    >
                        Filter
                    </Button>
                </Box>

                {/* Tabs */}
                <Tabs 
                    value={selectedTab} 
                    onChange={(e, newValue) => setSelectedTab(newValue)}
                    sx={{ borderBottom: 1, borderColor: 'divider' }}
                    variant="fullWidth"
                >
                    <Tab label={`All (${notifications.length})`} />
                    <Tab label={`Alerts (${alerts.length})`} />
                    <Tab label={`Unread (${unreadCount})`} />
                </Tabs>

                {/* Notifications List */}
                <Box sx={{ flex: 1, overflow: 'auto' }}>
                    {selectedTab === 0 && (
                        <List sx={{ p: 0 }}>
                            {notifications.length === 0 ? (
                                <Box sx={{ p: 4, textAlign: 'center' }}>
                                    <Notifications sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                                    <Typography color="text.secondary">
                                        No notifications yet
                                    </Typography>
                                </Box>
                            ) : (
                                notifications.map((notification, index) => (
                                    <React.Fragment key={notification.id}>
                                        <ListItem
                                            onClick={() => handleNotificationAction(notification)}
                                            sx={{
                                                cursor: 'pointer',
                                                bgcolor: notification.read ? 'transparent' : 'action.hover',
                                                '&:hover': { bgcolor: 'action.selected' }
                                            }}
                                            secondaryAction={
                                                <Box>
                                                    <Tooltip title="Mark as read">
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => { e.stopPropagation(); handleMarkAsRead(notification.id); }}
                                                        >
                                                            <MarkEmailRead />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Delete">
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => { e.stopPropagation(); handleDelete(notification.id); }}
                                                        >
                                                            <Delete />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            }
                                        >
                                            <ListItemAvatar>
                                                <Avatar sx={{ bgcolor: `${notification.color || 'primary'}.light` }}>
                                                    {getNotificationIcon(notification.type)}
                                                </Avatar>
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                        <Typography 
                                                            variant="body2" 
                                                            sx={{ 
                                                                fontWeight: notification.read ? 400 : 600,
                                                                flex: 1
                                                            }}
                                                        >
                                                            {notification.title}
                                                        </Typography>
                                                        {notification.severity && (
                                                            <Chip 
                                                                label={notification.severity}
                                                                size="small"
                                                                color={getNotificationColor(notification.severity)}
                                                            />
                                                        )}
                                                    </Box>
                                                }
                                                secondary={
                                                    <Box>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {notification.message}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                                            {formatDistanceToNow(new Date(notification.timestamp))} ago
                                                        </Typography>
                                                        {notification.actionable && (
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                onClick={(e) => { e.stopPropagation(); handleNotificationAction(notification); }}
                                                                sx={{ mt: 1 }}
                                                            >
                                                                {notification.action_text}
                                                            </Button>
                                                        )}
                                                    </Box>
                                                }
                                            />
                                        </ListItem>
                                        {index < notifications.length - 1 && <Divider />}
                                    </React.Fragment>
                                ))
                            )}
                        </List>
                    )}

                    {selectedTab === 1 && (
                        <List sx={{ p: 2 }}>
                            {alerts.length === 0 ? (
                                <Box sx={{ p: 4, textAlign: 'center' }}>
                                    <CheckCircle sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
                                    <Typography color="text.secondary">
                                        No active alerts
                                    </Typography>
                                </Box>
                            ) : (
                                alerts.map((alert) => (
                                    <Alert 
                                        key={alert.id}
                                        severity={alert.severity}
                                        sx={{ mb: 2 }}
                                        action={
                                            alert.actionable && (
                                                <Button color="inherit" size="small" onClick={() => handleNotificationAction(alert)}>
                                                    {alert.action_text}
                                                </Button>
                                            )
                                        }
                                    >
                                        <Typography variant="subtitle2" gutterBottom>
                                            {alert.title}
                                        </Typography>
                                        <Typography variant="body2">
                                            {alert.message}
                                        </Typography>
                                    </Alert>
                                ))
                            )}
                        </List>
                    )}

                    {selectedTab === 2 && (
                        <List sx={{ p: 0 }}>
                            {notifications.filter(n => !n.read).length === 0 ? (
                                <Box sx={{ p: 4, textAlign: 'center' }}>
                                    <CheckCircle sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
                                    <Typography color="text.secondary">
                                        All caught up!
                                    </Typography>
                                </Box>
                            ) : (
                                notifications.filter(n => !n.read).map((notification, index) => (
                                    <React.Fragment key={notification.id}>
                                        <ListItem
                                            onClick={() => handleNotificationAction(notification)}
                                            sx={{ cursor: 'pointer', bgcolor: 'action.hover', '&:hover': { bgcolor: 'action.selected' } }}
                                            secondaryAction={
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => { e.stopPropagation(); handleMarkAsRead(notification.id); }}
                                                >
                                                    <MarkEmailRead />
                                                </IconButton>
                                            }
                                        >
                                            <ListItemAvatar>
                                                <Avatar sx={{ bgcolor: `${notification.color || 'primary'}.light` }}>
                                                    {getNotificationIcon(notification.type)}
                                                </Avatar>
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={notification.title}
                                                secondary={
                                                    <Box>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {notification.message}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {formatDistanceToNow(new Date(notification.timestamp))} ago
                                                        </Typography>
                                                    </Box>
                                                }
                                            />
                                        </ListItem>
                                        {index < notifications.filter(n => !n.read).length - 1 && <Divider />}
                                    </React.Fragment>
                                ))
                            )}
                        </List>
                    )}
                </Box>
            </Drawer>

            {/* Filter Menu */}
            <Menu
                anchorEl={filterAnchorEl}
                open={Boolean(filterAnchorEl)}
                onClose={() => setFilterAnchorEl(null)}
            >
                <MenuItem>All Notifications</MenuItem>
                <MenuItem>Milestones</MenuItem>
                <MenuItem>Payments</MenuItem>
                <MenuItem>Messages</MenuItem>
                <MenuItem>Alerts</MenuItem>
            </Menu>

            {/* Settings Dialog */}
            <Dialog
                open={settingsOpen}
                onClose={() => setSettingsOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogCloseButton onClick={() => setSettingsOpen(false)} />
                <DialogTitle>
                    Notification Preferences
                </DialogTitle>
                <DialogContent>
                    <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                        Notification Channels
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
                        <FormControlLabel
                            control={
                                <Switch 
                                    checked={preferences.email}
                                    onChange={(e) => setPreferences({...preferences, email: e.target.checked})}
                                />
                            }
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Email fontSize="small" />
                                    Email Notifications
                                </Box>
                            }
                        />
                        <FormControlLabel
                            control={
                                <Switch 
                                    checked={preferences.sms}
                                    onChange={(e) => setPreferences({...preferences, sms: e.target.checked})}
                                />
                            }
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Sms fontSize="small" />
                                    SMS Notifications
                                </Box>
                            }
                        />
                        <FormControlLabel
                            control={
                                <Switch 
                                    checked={preferences.whatsapp}
                                    onChange={(e) => setPreferences({...preferences, whatsapp: e.target.checked})}
                                />
                            }
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <WhatsApp fontSize="small" />
                                    WhatsApp Notifications
                                </Box>
                            }
                        />
                        <FormControlLabel
                            control={
                                <Switch 
                                    checked={preferences.push}
                                    onChange={(e) => setPreferences({...preferences, push: e.target.checked})}
                                />
                            }
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <NotificationsActive fontSize="small" />
                                    Push Notifications
                                </Box>
                            }
                        />
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle2" gutterBottom>
                        Alert Types
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
                        {Object.entries(preferences.alert_types).map(([key, value]) => (
                            <FormControlLabel
                                key={key}
                                control={
                                    <Switch 
                                        checked={value}
                                        onChange={(e) => setPreferences({
                                            ...preferences,
                                            alert_types: {
                                                ...preferences.alert_types,
                                                [key]: e.target.checked
                                            }
                                        })}
                                    />
                                }
                                label={key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            />
                        ))}
                    </Box>

                    <Alert severity="info">
                        Critical alerts (payment failures, security issues) will always be sent regardless of preferences.
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSettingsOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSavePreferences}>
                        Save Preferences
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

// Mock data helper
const getMockNotifications = () => {
    const now = Date.now();
    return {
        notifications: [
            {
                id: 'notif_001',
                type: 'milestone',
                title: 'Foundation Milestone Approved',
                message: 'Your foundation work has been verified and approved. Payment of $7,500 released to contractor.',
                timestamp: now - 2 * 60 * 60 * 1000,
                read: false,
                severity: 'success',
                color: 'success',
                actionable: true,
                action_text: 'View Details'
            },
            {
                id: 'notif_002',
                type: 'message',
                title: 'New Message from John Moyo',
                message: 'Walls construction ahead of schedule. Expecting completion by Friday.',
                timestamp: now - 5 * 60 * 60 * 1000,
                read: false,
                severity: 'info',
                color: 'info',
                actionable: true,
                action_text: 'Reply'
            },
            {
                id: 'notif_003',
                type: 'alert',
                title: 'Weather Alert',
                message: 'Heavy rainfall expected this week. Outdoor work may be delayed by 2-3 days.',
                timestamp: now - 24 * 60 * 60 * 1000,
                read: true,
                severity: 'warning',
                color: 'warning',
                actionable: false
            },
            {
                id: 'notif_004',
                type: 'payment',
                title: 'Payment Due Soon',
                message: 'Roofing milestone payment of $7,500 due in 3 days.',
                timestamp: now - 36 * 60 * 60 * 1000,
                read: true,
                severity: 'info',
                color: 'primary',
                actionable: true,
                action_text: 'Make Payment'
            }
        ],
        alerts: [
            {
                id: 'alert_001',
                severity: 'warning',
                title: 'Budget Alert',
                message: 'Current spending is 5% above projected timeline. Consider reviewing upcoming milestones.',
                actionable: true,
                action_text: 'Review Budget'
            },
            {
                id: 'alert_002',
                severity: 'info',
                title: 'Rainy Season Approaching',
                message: 'November-March is rainy season in Zimbabwe. Outdoor work may experience delays.',
                actionable: true,
                action_text: 'Adjust Timeline'
            }
        ]
    };
};

export default EnhancedNotificationCenter;
